import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// File: /home/ubuntu/avaai_dashboard/frontend/src/pages/Account.tsx
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";
import ConnectionModeStatusBadge from "../components/ConnectionModeStatusBadge";
import { hardLogout } from "../utils/auth";
const API_BASE = import.meta.env.VITE_API_BASE ||
    "";
const TOKEN_KEYS = ["ava_token", "token", "access_token"];
function getAnyToken() {
    for (const key of TOKEN_KEYS) {
        const localValue = localStorage.getItem(key);
        if (localValue && localValue.trim().length > 10)
            return localValue.trim();
        const sessionValue = sessionStorage.getItem(key);
        if (sessionValue && sessionValue.trim().length > 10)
            return sessionValue.trim();
    }
    return "";
}
function readLocalProfile() {
    const candidates = ["ava_profile", "profile", "ava_user", "user_profile", "user"];
    for (const key of candidates) {
        try {
            const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
            if (!raw)
                continue;
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object") {
                return parsed;
            }
        }
        catch {
            // ignore malformed storage
        }
    }
    return {};
}
function prettyValue(value, fallback = "—") {
    if (!value || !String(value).trim())
        return fallback;
    return String(value).trim();
}
function inferProvider(user, localProfile) {
    const explicit = user.provider ||
        localProfile.provider ||
        localProfile.auth_provider ||
        localProfile.loginProvider;
    if (explicit)
        return String(explicit);
    const email = (user.email || user.sub || "").toLowerCase();
    if (email.includes("gmail.com"))
        return "Google / Email";
    return "Email / Local";
}
function inferPlan(user, localProfile) {
    return (user.plan ||
        user.subscription_status ||
        user.subscription ||
        localProfile.plan ||
        localProfile.subscription ||
        localProfile.membership ||
        "Active");
}
function inferDisplayName(user, localProfile) {
    return (user.display_name ||
        user.name ||
        user.full_name ||
        user.username ||
        localProfile.displayName ||
        localProfile.display_name ||
        localProfile.name ||
        "");
}
function inferPreferredName(displayName, email) {
    if (displayName && displayName !== "—")
        return displayName;
    if (email && email !== "—") {
        const first = email.split("@")[0]?.trim();
        if (first)
            return first;
    }
    return "Ava User";
}
const Account = () => {
    const { themeColors } = useContext(ThemeContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [error, setError] = useState("");
    const [lastSync, setLastSync] = useState("");
    const [refreshNonce, setRefreshNonce] = useState(0);
    const [refreshMessage, setRefreshMessage] = useState("");
    const localProfile = useMemo(() => readLocalProfile(), []);
    const token = useMemo(() => getAnyToken(), []);
    useEffect(() => {
        let cancelled = false;
        async function loadAccount() {
            setLoading(true);
            setError("");
            try {
                const headers = {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                };
                if (token) {
                    headers.Authorization = `Bearer ${token}`;
                }
                const response = await fetch(`${API_BASE}/api/v1/auth/me`, { credentials: "include",
                    method: "GET",
                    headers,
                });
                if (response.status === 401) {
                    if (!cancelled) {
                        setUser(null);
                        setError("Not logged in or session expired.");
                    }
                    return;
                }
                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data?.ok) {
                    if (!cancelled) {
                        setUser(null);
                        setError(isAdmin ? "Failed to load account details from the API." : "Account sync temporarily unavailable.");
                    }
                    return;
                }
                if (!cancelled) {
                    setUser(data.user || null);
                    setLastSync(new Date().toLocaleString());
                }
            }
            catch {
                if (!cancelled) {
                    setUser(null);
                    setError("Network error while loading account details.");
                }
            }
            finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }
        void loadAccount();
        return () => {
            cancelled = true;
        };
    }, [token, refreshNonce]);
    const email = prettyValue(user?.email || user?.sub || localProfile.email);
    const rawDisplayName = inferDisplayName(user || {}, localProfile);
    const preferredName = inferPreferredName(rawDisplayName, email);
    const role = prettyValue(user?.role || localProfile.role || "member");
    const zip = prettyValue(user?.zip ||
        user?.zip_code ||
        user?.postal_code ||
        localProfile.zip ||
        localProfile.zip_code ||
        localProfile.postal_code ||
        localProfile.profile?.zip ||
        localProfile.profile?.zip_code ||
        localProfile.address?.zip ||
        localProfile.address?.postal_code);
    const userId = prettyValue(String(user?.id ??
        user?.user_id ??
        user?.sub ??
        localProfile.id ??
        localProfile.user_id ??
        localProfile.sub ??
        ""));
    const provider = prettyValue(inferProvider(user || {}, localProfile));
    const rawPlan = String(inferPlan(user || {}, localProfile) || "").toLowerCase();
    const normalizedRole = String(user?.role || localProfile.role || "").trim().toLowerCase();
    const normalizedSubscription = String(user?.subscription_status ||
        localProfile.subscription_status ||
        "").trim().toLowerCase();
    const offlineEntitlement = String(user?.offline_entitlement ||
        localProfile.offline_entitlement ||
        "").trim().toLowerCase();
    const isOfflineAdminEntitlement = offlineEntitlement === "admin";
    const vipLifetime = Boolean(user?.vip_lifetime ??
        localProfile.vip_lifetime ??
        false);
    const isAdmin = [
        "admin",
        "owner",
        "superadmin",
        "super_admin",
    ].includes(normalizedRole);
    const isVip = normalizedRole === "vip" ||
        vipLifetime;
    const isPro = isVip ||
        normalizedSubscription === "active" ||
        normalizedSubscription === "pro" ||
        rawPlan === "active" ||
        rawPlan === "vip" ||
        rawPlan === "pro";
    const plan = isAdmin
        ? "Admin"
        : isOfflineAdminEntitlement
            ? "Admin — Local Mode"
            : isPro
                ? "Pro"
                : "Free";
    const accountStatus = prettyValue(user?.status ||
        user?.subscription_status ||
        localProfile.status ||
        (token ? "Signed in" : "Local session"));
    const language = prettyValue(localProfile.language ||
        localProfile.locale ||
        localStorage.getItem("ava_language") ||
        sessionStorage.getItem("ava_language") ||
        "English");
    const voice = prettyValue(localProfile.voice ||
        localProfile.voiceName ||
        localStorage.getItem("ava_voice") ||
        sessionStorage.getItem("ava_voice") ||
        "Sonia / Default");
    const themeName = prettyValue(localProfile.theme ||
        localStorage.getItem("ava_theme") ||
        localStorage.getItem("theme") ||
        sessionStorage.getItem("ava_theme") ||
        sessionStorage.getItem("theme") ||
        "Neon");
    const cancelAtPeriodEnd = !!user?.subscription_cancel_at_period_end;
    const renewalDate = isVip
        ? "No renewal required"
        : user?.subscription_current_period_end
            ? new Date(user.subscription_current_period_end).toLocaleString()
            : isAdmin
                ? "Administrative access"
                : isOfflineAdminEntitlement
                    ? "Administrative actions require internet"
                    : isPro
                        ? "Managed by Apple"
                        : "Free plan";
    const apiStatus = loading ? "Checking..." : error ? "Sync unavailable" : "Connected";
    const accountIsMobile = typeof window !== "undefined" && window.innerWidth <= 820;
    const handleMembershipManagerOpen = () => {
        navigate("/subscribe");
    };
    const handleDeleteAccountWarning = () => {
        const confirmed = window.confirm("Are you sure you want to continue to account deletion? This action cannot be reversed once your account is deleted.");
        if (confirmed) {
            navigate("/subscribe");
        }
    };
    const shellStyle = {
        minHeight: "100%",
        width: "100%",
        padding: "18px 16px 120px",
        color: themeColors.text,
        fontFamily: "Orbitron, sans-serif",
        background: "radial-gradient(circle at top, rgba(0,255,255,0.08) 0%, rgba(0,0,0,0) 34%), linear-gradient(180deg, rgba(6,10,18,0.98) 0%, rgba(3,6,12,1) 100%)",
        boxSizing: "border-box",
        overflowY: "auto",
    };
    const pageStyle = {
        maxWidth: 1450,
        margin: "0 auto",
    };
    const compactGrid = {
        display: "grid",
        gridTemplateColumns: accountIsMobile ? "1fr" : "1.4fr 1fr",
        gap: 16,
        alignItems: "stretch",
        marginBottom: 16,
    };
    const summaryGrid = {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 12,
        marginTop: 18,
    };
    const infoGrid = {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 16,
    };
    const heroStyle = {
        border: `1px solid ${themeColors.accent}40`,
        borderRadius: 24,
        padding: "22px 22px 18px",
        background: "rgba(8, 14, 26, 0.82)",
        boxShadow: `0 0 28px ${themeColors.accent}18`,
        backdropFilter: "blur(10px)",
        minHeight: 230,
    };
    const cardStyle = {
        border: `1px solid ${themeColors.accent}38`,
        borderRadius: 22,
        padding: 18,
        background: "rgba(8, 13, 24, 0.84)",
        boxShadow: `0 0 18px ${themeColors.accent}15`,
        minHeight: 180,
    };
    const miniCardStyle = {
        border: `1px solid ${themeColors.accent}28`,
        borderRadius: 16,
        padding: "12px 14px",
        background: "rgba(255,255,255,0.03)",
        minHeight: 88,
    };
    const sectionTitleStyle = {
        color: "#8ff7ff",
        margin: "0 0 14px",
        fontSize: 20,
        letterSpacing: 1.2,
        opacity: 1,
        textShadow: "0 0 10px rgba(0,240,255,0.35)",
    };
    const labelStyle = {
        fontSize: 12,
        letterSpacing: 1.15,
        textTransform: "uppercase",
        color: "rgba(235, 252, 255, 0.92)",
        opacity: 1,
        fontWeight: 800,
        marginBottom: 5,
    };
    const valueStyle = {
        fontSize: 16,
        fontWeight: 800,
        lineHeight: 1.35,
        wordBreak: "break-word",
        color: "#ffffff",
        opacity: 1,
        textShadow: "0 0 8px rgba(255,255,255,0.16)",
    };
    const pairStyle = {
        marginBottom: 12,
    };
    const buttonRowStyle = {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: 10,
        marginTop: 12,
    };
    const buttonBase = {
        appearance: "none",
        border: `1px solid ${themeColors.accent}`,
        background: themeColors.buttonBg,
        color: themeColors.text,
        padding: "12px 14px",
        borderRadius: 12,
        cursor: "pointer",
        fontFamily: "Orbitron, sans-serif",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.45,
        boxShadow: `0 0 14px ${themeColors.accent}18`,
        minHeight: 46,
        width: accountIsMobile ? "100%" : undefined,
        boxSizing: "border-box",
    };
    const primaryButton = {
        ...buttonBase,
        background: `linear-gradient(135deg, ${themeColors.accent}22, rgba(255,255,255,0.08))`,
    };
    const dangerButton = {
        ...buttonBase,
        border: "1px solid rgba(255,120,120,0.75)",
        boxShadow: "0 0 14px rgba(255,120,120,0.18)",
        color: "#ffd7d7",
    };
    const statusBadgeStyle = {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        border: `1px solid ${themeColors.accent}55`,
        borderRadius: 999,
        padding: "8px 12px",
        fontSize: 12,
        color: "#7cf7ff",
        background: "rgba(0, 20, 34, 0.72)",
        marginTop: 10,
    };
    const tinyNote = {
        fontSize: 12,
        color: "rgba(236, 250, 255, 0.88)",
        opacity: 1,
        lineHeight: 1.5,
    };
    const statusOk = !error && !loading;
    return (_jsx("div", { style: shellStyle, children: _jsxs("div", { style: pageStyle, children: [_jsxs("div", { style: compactGrid, className: "account-top-grid", children: [_jsxs("section", { style: heroStyle, children: [_jsx("div", { style: {
                                        color: "#8ff7ff",
                                        fontSize: 12,
                                        letterSpacing: 2,
                                        textTransform: "uppercase",
                                        opacity: 1,
                                        marginBottom: 8,
                                        textShadow: "0 0 10px rgba(0,240,255,0.35)",
                                        fontWeight: 900,
                                    }, children: "AVAAIO Account Center" }), _jsx("h1", { style: {
                                        margin: 0,
                                        fontSize: "clamp(2rem, 4vw, 3.25rem)",
                                        color: themeColors.text,
                                        lineHeight: 1.02,
                                    }, children: preferredName }), _jsx("div", { style: {
                                        marginTop: 12,
                                        fontSize: 15,
                                        opacity: 1,
                                        maxWidth: 860,
                                        lineHeight: 1.5,
                                    }, children: "Review your AVAAIO identity, membership, security, billing, and account tools from one clean control page." }), _jsxs("div", { style: statusBadgeStyle, children: [_jsx("span", { children: "\u25CF" }), _jsx("span", { children: loading
                                                ? "Loading..."
                                                : statusOk
                                                    ? "Signed in and synced"
                                                    : isAdmin ? "Needs attention" : "Sync unavailable" })] }), _jsxs("div", { style: summaryGrid, children: [_jsxs("div", { style: miniCardStyle, children: [_jsx("div", { style: labelStyle, children: "Membership" }), _jsx("div", { style: valueStyle, children: loading ? "Loading..." : plan })] }), _jsxs("div", { style: miniCardStyle, children: [_jsx("div", { style: labelStyle, children: "Session" }), _jsx("div", { style: valueStyle, children: token ? "Signed In" : "Guest / Local" })] }), _jsxs("div", { style: miniCardStyle, children: [_jsx("div", { style: labelStyle, children: "Account Sync" }), _jsx("div", { style: valueStyle, children: apiStatus })] }), !isVip ? (_jsxs("div", { style: miniCardStyle, children: [_jsx("div", { style: labelStyle, children: "Renewal / End" }), _jsx("div", { style: valueStyle, children: loading ? "Loading..." : renewalDate })] })) : null] }), _jsx("div", { style: { marginTop: 12, ...tinyNote }, children: lastSync ? `Last sync: ${lastSync}` : "Waiting for account sync..." })] }), _jsxs("section", { style: { ...cardStyle, minHeight: 230 }, children: [_jsx("h2", { style: sectionTitleStyle, children: "Quick Actions" }), _jsxs("div", { style: buttonRowStyle, children: [_jsx("button", { style: buttonBase, onClick: () => navigate("/profile"), children: "Open Profile" }), _jsx("button", { style: primaryButton, onClick: handleMembershipManagerOpen, children: "Membership Manager" }), _jsx("button", { style: buttonBase, onClick: () => navigate("/subscribe"), children: "Billing" }), _jsx("button", { style: buttonBase, onClick: () => {
                                                setRefreshMessage("Refreshing account sync...");
                                                setRefreshNonce((n) => n + 1);
                                                window.setTimeout(() => setRefreshMessage("Account sync refreshed."), 450);
                                                window.setTimeout(() => setRefreshMessage(""), 2400);
                                            }, children: "Refresh Sync" }), isAdmin ? (_jsx("button", { style: buttonBase, onClick: () => navigate("/admin"), children: "Admin Console" })) : null, _jsx("button", { style: dangerButton, onClick: hardLogout, children: "Logout" })] }), _jsx("div", { style: { marginTop: 12, ...tinyNote }, children: refreshMessage ? refreshMessage : "Use Membership Manager to review subscription changes and account options." })] })] }), error ? (_jsx("div", { style: {
                        marginBottom: 16,
                        border: "1px solid rgba(255,120,120,0.5)",
                        background: "rgba(120,20,20,0.18)",
                        color: "#ffd0d0",
                        padding: "14px 16px",
                        borderRadius: 16,
                    }, children: error })) : null, _jsxs("div", { style: infoGrid, children: [_jsxs("section", { style: cardStyle, children: [_jsx("h2", { style: sectionTitleStyle, children: "Identity" }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Display Name" }), _jsx("div", { style: valueStyle, children: loading ? "Loading..." : preferredName })] }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Email" }), "                ", _jsx("div", { style: {
                                                ...valueStyle,
                                                textTransform: "none",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                overflowWrap: "normal",
                                                wordBreak: "normal",
                                                letterSpacing: "0.01em",
                                                fontSize: "clamp(0.72rem, 0.95vw, 0.92rem)",
                                                maxWidth: "100%",
                                            }, title: email, children: loading ? "Loading..." : email })] }), _jsxs("div", { children: [_jsx("div", { style: labelStyle, children: "Sign-in Provider" }), _jsx("div", { style: valueStyle, children: loading ? "Loading..." : provider })] })] }), _jsxs("section", { style: cardStyle, children: [_jsx("h2", { style: sectionTitleStyle, children: "Profile Details" }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "ZIP" }), _jsx("div", { style: valueStyle, children: loading ? "Loading..." : zip })] }), isAdmin ? (_jsxs(_Fragment, { children: [_jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Profile Source" }), _jsx("div", { style: valueStyle, children: Object.keys(localProfile).length > 0 ? "Local + API" : "API only" })] }), _jsxs("div", { children: [_jsx("div", { style: labelStyle, children: "User ID" }), _jsx("div", { style: valueStyle, children: loading ? "Loading..." : prettyValue(String(user?.id ?? "")) })] })] })) : null] }), _jsxs("section", { style: cardStyle, children: [_jsx("h2", { style: sectionTitleStyle, children: "Membership" }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Plan" }), _jsx("div", { style: valueStyle, children: loading ? "Loading..." : plan })] }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Subscription State" }), _jsx("div", { style: valueStyle, children: loading ? "Loading..." : accountStatus })] }), !isVip ? (_jsxs(_Fragment, { children: [_jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Scheduled Cancel" }), _jsx("div", { style: valueStyle, children: loading ? "Loading..." : cancelAtPeriodEnd ? "Yes" : "No" })] }), _jsxs("div", { children: [_jsx("div", { style: labelStyle, children: "Renewal / End" }), _jsx("div", { style: valueStyle, children: loading ? "Loading..." : renewalDate })] })] })) : null] }), _jsxs("section", { style: cardStyle, children: [_jsx("h2", { style: sectionTitleStyle, children: "AI Personalization" }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Preferred Name" }), _jsx("div", { style: valueStyle, children: preferredName })] }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Language" }), _jsx("div", { style: valueStyle, children: language })] }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Voice" }), _jsx("div", { style: valueStyle, children: voice })] }), _jsxs("div", { children: [_jsx("div", { style: labelStyle, children: "Theme" }), _jsx("div", { style: valueStyle, children: themeName })] })] }), _jsx(ConnectionModeStatusBadge, {}), _jsxs("section", { style: cardStyle, children: [_jsx("h2", { style: sectionTitleStyle, children: "Ava Control Center" }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "AI Brain" }), _jsx("div", { style: valueStyle, children: token ? "Online" : "Limited" })] }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Voice Engine" }), _jsx("div", { style: valueStyle, children: "Ready" })] }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Memory Engine" }), _jsx("div", { style: valueStyle, children: "Active" })] }), _jsxs("div", { children: [_jsx("div", { style: labelStyle, children: "Cloud Session" }), _jsx("div", { style: valueStyle, children: token ? "Connected" : "Offline / Local" })] })] }), !isAdmin ? (_jsxs("section", { style: cardStyle, children: [_jsx("h2", { style: sectionTitleStyle, children: "Security" }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Session" }), _jsx("div", { style: valueStyle, children: token ? "Signed In" : "Guest / Local" })] }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Account Protection" }), _jsx("div", { style: valueStyle, children: token ? "Protected" : "Limited" })] }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Cloud Session" }), _jsx("div", { style: valueStyle, children: apiStatus === "Connected" ? "Connected" : "Limited" })] }), _jsx("div", { style: tinyNote, children: "Your session is protected. More security controls will appear here as they become available." })] })) : (_jsxs("section", { style: cardStyle, children: [_jsx("h2", { style: sectionTitleStyle, children: "Admin Diagnostics" }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Token State" }), _jsx("div", { style: valueStyle, children: token ? "Present" : "Missing" })] }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Current Access" }), _jsx("div", { style: valueStyle, children: "Administrator" })] }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "API Authorization" }), _jsx("div", { style: valueStyle, children: token ? "Bearer token loaded" : "No active bearer token" })] }), _jsx("div", { style: tinyNote, children: "Advanced diagnostics are only visible to administrators." })] })), _jsxs("section", { style: cardStyle, children: [_jsx("h2", { style: sectionTitleStyle, children: "Account Management" }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Membership Options" }), _jsx("div", { style: valueStyle, children: "Manage your subscription and account settings" })] }), _jsxs("div", { style: pairStyle, children: [_jsx("div", { style: labelStyle, children: "Account Changes" }), _jsx("div", { style: valueStyle, children: "Open the membership page to review subscription updates or account removal options" })] }), _jsxs("div", { style: buttonRowStyle, children: [_jsx("button", { style: primaryButton, onClick: handleMembershipManagerOpen, children: "Open Membership Manager" }), _jsx("button", { style: buttonBase, onClick: () => navigate("/subscribe"), children: "Open Billing" }), _jsx("button", { style: buttonBase, onClick: () => {
                                                try {
                                                    window.location.href =
                                                        "https://apps.apple.com/account/subscriptions";
                                                }
                                                catch (err) {
                                                    console.error(err);
                                                }
                                            }, children: "Manage Apple Subscription" }), _jsx("button", { style: buttonBase, onClick: async () => {
                                                try {
                                                    alert("Restore request started. If an Apple subscription exists for this Apple ID, access may refresh automatically.");
                                                    await fetch(`${API_BASE}/api/v1/billing/apple/confirm`, {
                                                        method: "POST",
                                                        credentials: "include",
                                                        headers: {
                                                            "Content-Type": "application/json",
                                                        },
                                                        body: JSON.stringify({
                                                            restore: true,
                                                        }),
                                                    });
                                                }
                                                catch (err) {
                                                    console.error("Restore purchases failed:", err);
                                                    alert("Unable to restore purchases right now.");
                                                }
                                            }, children: "Restore Purchases" }), _jsx("button", { style: buttonBase, onClick: () => navigate("/forgot-password"), children: "Change Password" }), _jsx("button", { style: {
                                                ...buttonBase,
                                                opacity: 1,
                                                cursor: "not-allowed",
                                            }, onClick: () => alert("Secure email change is being prepared. New email verification, old email notification, and stable user ID memory protection will be enabled before this goes live."), children: "Change Email" }), _jsx("button", { style: dangerButton, onClick: handleDeleteAccountWarning, children: "Delete Account" })] }), _jsx("div", { style: tinyNote, children: "Deleting your account is permanent and cannot be reversed once completed." })] })] })] }) }));
};
export default Account;
